/**
 * AI 支付助手打点：SDK / Chat App → webstorev35DataLayer
 *
 * SDK 通过 createLifecycleReporter 回调 track；
 * 宿主自定义气泡、主动气泡在业务侧手动调用 trackPayAssistantPoint。
 */

/** @typedef {Record<string, unknown>} PayAssistantTrackProps */

import { pushTrackEvent } from '@utiles/officialTopupNew/trackEvent';

/** SDK action → 本仓库 BI eventName（createLifecycleReporter 路径） */
const SDK_ACTION_NAME_MAP = {
  entry_show: 'spirit_show',
  entry_click: 'spirit_click',
  feedback_up: 'feedback_good',
  feedback_down: 'feedback_bad',
  chat_start: 'user_ask',
  dialog_shown: 'chat_open',
};

/**
 * @param {string} action
 * @returns {string}
 */
function mapSdkActionToEventName(action) {
  return SDK_ACTION_NAME_MAP[action] || action;
}

/**
 * 写入 v3.5 支付页 BI 队列
 * @param {PayAssistantTrackProps & {
 *   action?: string,
 *   category?: string,
 *   actor?: string,
 * }} payload
 */
export function trackPayAssistantPoint(payload = {}) {
  try {
    const {
      action = '',
      category = '',
      actor = '',
      ...rest
    } = payload;

    pushTrackEvent({
      eventType: 'pay_assistant',
      eventName: action,
      eventReason: category,
      eventValue: {
        actor,
        ...rest,
      },
    });
  } catch (e) {
    // 打点失败不影响主流程
  }
}

/**
 * createLifecycleReporter.track 适配
 * @param {string} eventName
 * @param {PayAssistantTrackProps} props
 */
export function trackPayAssistantFromReporter(eventName, props = {}) {
  const { category = '', action = '', actor = '', ...rest } = props || {};
  const resolvedAction = action || eventName;
  const mappedAction = mapSdkActionToEventName(resolvedAction);
  let trackRest = rest;
  // tip_show 的 style/texts 为 UI 载荷，不上报 BI，其余字段照常上报
  if (resolvedAction === 'tip_show') {
    const { style, texts, ...restWithoutUiPayload } = rest;
    trackRest = restWithoutUiPayload;
  }
  trackPayAssistantPoint({
    action: mappedAction,
    category,
    actor,
    sdkEventName: eventName,
    ...trackRest,
  });
}

/**
 * SDK onError
 * @param {{ code?: string, message?: string, details?: unknown } | Record<string, unknown>} error
 */
export function trackPayAssistantSdkError(error = {}) {
  trackPayAssistantPoint({
    action: 'sdk_error',
    category: 'error',
    actor: 'sdk',
    code: error.code,
    message: error.message,
    details: error.details,
  });
}

/** 同页仅上报一次，便于监控 CDN SDK 版本 */
let lifecycleReporterMissingReported = false;

/**
 * SDK 缺少 createLifecycleReporter 时上报（仅一次）
 */
function reportLifecycleReporterMissing() {
  if (lifecycleReporterMissingReported) return;
  lifecycleReporterMissingReported = true;
  trackPayAssistantPoint({
    action: 'lifecycle_reporter_missing',
    category: 'error',
    actor: 'host',
    api: 'createLifecycleReporter',
  });
  console.warn(
    '[PayAssistant] AgentChat.createLifecycleReporter 不可用，SDK 生命周期打点未接入',
  );
}

/**
 * 生成 AgentChatConfig.onEvent（依赖 SDK createLifecycleReporter）
 * @param {(type: string, data: unknown) => void} passthrough
 * @param {Function | undefined} createLifecycleReporter
 * @returns {(type: string, data: unknown) => void}
 */
export function createPayAssistantOnEvent(passthrough, createLifecycleReporter) {
  if (typeof createLifecycleReporter !== 'function') {
    reportLifecycleReporterMissing();
    return (type, data) => {
      passthrough?.(type, data);
    };
  }
  return createLifecycleReporter({
    track: trackPayAssistantFromReporter,
    passthrough,
    // SDK_ERROR 经 onError 上报，避免与 onEvent 双发
    reportError: false,
  });
}

/**
 * 宿主侧交互打点（自定义入口气泡 / 主动气泡按钮等）
 * @param {string} action
 * @param {PayAssistantTrackProps} [props]
 */
export function trackPayAssistantHostAction(action, props = {}) {
  trackPayAssistantPoint({
    action,
    category: props.category || 'tip',
    actor: props.actor || 'host',
    ...props,
  });
}

/**
 * Chat App 快捷入口点击（NEED_HELP_ENTRY_CLICK）
 * @param {{ entry?: string, handler?: string, gameId?: number } | Record<string, unknown>} [payload]
 */
export function trackNeedHelpEntryClick(payload = {}) {
  const { entry, handler, gameId } = payload;
  trackPayAssistantHostAction('quick_action_click', {
    category: 'chat',
    actor: 'chat_app',
    entry,
    handler,
    ...(gameId != null ? { gameId } : {}),
  });
}

/**
 * Chat App 优惠券卡「立即使用」（COUPON_USE_CLICK）
 * @param {{ couponId?: string | number } | Record<string, unknown>} [payload]
 */
export function trackCouponUseClick(payload = {}) {
  const rawId = payload.couponId;
  const couponId =
    rawId == null || rawId === '' ? '' : String(rawId);
  trackPayAssistantHostAction('coupon_use_click', {
    category: 'chat',
    actor: 'chat_app',
    couponId,
  });
}
